// Shared types used across frontend and functions

export type OrderStatus =
  | 'received'
  | 'design'
  | 'production'
  | 'ready_to_ship'
  | 'shipped'
  | 'completed'
  | 'cancelled'

export type InvoiceStatus =
  | 'shipped'
  | 'paid'
  | 'cancelled'

export type Role = 'admin' | 'sales' | 'fulfillment' | 'accounting' | 'design'

export interface User {
  id: number
  email: string
  name: string
  roles: Role[]
}

export interface OrderFlag {
  id: number
  name: string
  type: 'country' | 'custom'
  image_url: string | null
  r2_key: string | null
}

export interface Order {
  id: number
  reference: string
  client_email: string
  client_name: string
  body: string | null
  status: OrderStatus
  flags: OrderFlag[]
  pdf_r2_key: string | null
  production_pdf_r2_key: string | null
  created_at: string
  updated_at: string
}

export interface OrderItem {
  id: number
  order_id: number
  flag_id: number
  flag_name: string | null
  flag_type: 'country' | 'custom' | null
  flag_image_url: string | null
  flag_r2_key: string | null
  quantity: number
  width_cm: number
  height_cm: number
  material: string
}

export const FLAG_MATERIALS = [
  'Polyester',
  'Nylon',
  'Baumwolle',
  'Satin',
  'Netz',
] as const

export type FlagMaterial = typeof FLAG_MATERIALS[number]

export interface StatusHistoryEntry {
  id: number
  order_id: number
  status: OrderStatus
  notes: string | null
  changed_by: number | null
  changed_by_name: string | null
  changed_at: string
}

export interface Invoice {
  id: number
  order_id: number
  invoice_number: string
  r2_key: string | null
  total_amount: number
  status: InvoiceStatus
  sent_at: string
  sent_by: number | null
  notes: string | null
  created_at: string
}

export interface Flag {
  id: number
  name: string
  type: 'country' | 'custom'
  slug: string | null
  image_url: string | null
  r2_key: string | null
  created_at: string
  updated_at: string
}

export interface Client {
  id: number
  name: string
  email: string | null
  phone: string | null
  company: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export const STATUS_LABELS: Record<OrderStatus, string> = {
  received: 'Eingegangen',
  design: 'Design',
  production: 'Produktion',
  ready_to_ship: 'Fertig zum Versand',
  shipped: 'Verschickt',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
}

export const NEXT_STATUSES: Partial<Record<OrderStatus, OrderStatus[]>> = {
  received:       ['design', 'cancelled'],
  design:         ['production', 'cancelled'],
  production:     ['ready_to_ship', 'cancelled'],
  ready_to_ship:  ['shipped', 'cancelled'],
  shipped:        ['completed', 'cancelled'],
}

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  shipped: 'Verschickt',
  paid: 'Bezahlt',
  cancelled: 'Abgebrochen',
}

export const ORDER_STATUSES: OrderStatus[] = [
  'received',
  'design',
  'production',
  'ready_to_ship',
  'shipped',
  'completed',
  'cancelled',
]

export const ACTIVE_ORDER_STATUSES: OrderStatus[] = ['received', 'design', 'production', 'ready_to_ship', 'shipped']
export const ARCHIVED_ORDER_STATUSES: OrderStatus[] = ['completed', 'cancelled']

export const INVOICE_STATUSES: InvoiceStatus[] = ['shipped', 'paid', 'cancelled']

export const ACTIVE_INVOICE_STATUSES: InvoiceStatus[] = ['shipped']
export const ARCHIVED_INVOICE_STATUSES: InvoiceStatus[] = ['paid', 'cancelled']
